import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Select,
  MenuItem,
} from '@mui/material';
import { useLazyGetUsersQuery } from '@api/userList';
import { SEARCH_DEBOUNCE_TIME, TOAST_TYPES } from '@constants/constants';
import useDebounce from '@hooks/useDebounce';
import {
  CreateReferrerPayload,
  ReferrerBankPayload,
  ReferrerType,
  UpdateReferrerPayload,
  useCreateReferrerBankMutation,
  useCreateReferrerMutation,
  useLazyGetReferrerBankQuery,
  useLazyGetReferrerByIdQuery,
  useUpdateReferrerMutation,
} from '@api/referrers';
import { setToastMessage } from '@stores/App/slice';
import { SelectWithLabel } from '@components';
import { useLazyGetCompaniesQuery } from '@api/company';
import { useUpdateBankDetailsMutation } from '@api/banks';
import {
  createFormattedPercentValue,
  handlePercentageChange,
  stripPercentSymbol,
} from '@utils/percentageInputsUtils';

const referrerTypeOptions = [
  { value: 'PERPETUAL', label: 'Perpetual' },
  { value: 'COHORT_BASED', label: 'Cohort Based' },
];

export interface ReferrerListItem {
  id: string;
  email: string;
  name: string;
  type: string;
  status: string;
  numberOfCohorts: number;
  activeDealCount: number;
}

interface ReferrerDetails {
  id?: string;
  email: string;
  name: string;
  companyId: string;
}

interface CommissionDetails {
  commissionPercentage: string;
  commissionType: string;
  commissionExpiry: string | null;
  numberOfCohorts: number;
}

interface BankDetails {
  id?: string;
  bankName: string;
  bankAddress: string;
  swiftCode: string;
  iban: string;
  accountNumber: string;
  routingNumber: string | null;
}

interface EmailUser {
  id: string;
  email: string;
  name: string;
  companyId: string;
}

export interface ReferrerData {
  id?: string | null;
  userDetails: ReferrerDetails;
  commissionDetails: CommissionDetails;
  bankDetails?: BankDetails;
  status?: string | null;
  activeDealCount?: number | null;
}

const ReferrerSetupForm: FC<{
  referrer: ReferrerListItem | null;
  handleCloseReferrerDetails: () => void;
}> = ({ referrer, handleCloseReferrerDetails }) => {
  const dispatch = useDispatch();

  const [referrerDetails, setReferrerDetails] = useState<ReferrerData>({
    id: null,
    userDetails: {
      id: '',
      email: '',
      name: '',
      companyId: '',
    },
    commissionDetails: {
      commissionPercentage: createFormattedPercentValue(5),
      commissionType: '',
      commissionExpiry: null,
      numberOfCohorts: 0,
    },
    bankDetails: {
      bankName: '',
      bankAddress: '',
      swiftCode: '',
      iban: '',
      accountNumber: '',
      routingNumber: null,
    },
    status: null,
    activeDealCount: 0,
  });
  const [companyOptions, setCompanyOptions] = useState<{ label: string; value: string }[]>([]);
  const [emailOptions, setEmailOptions] = useState<EmailUser[]>([]);
  const [emailInputValue, setEmailInputValue] = useState(referrerDetails?.userDetails.email || '');

  const [getCompanies] = useLazyGetCompaniesQuery();
  const [getUsers] = useLazyGetUsersQuery();
  const [createReferrer] = useCreateReferrerMutation();
  const [updateReferrer] = useUpdateReferrerMutation();
  const [getReferrerById] = useLazyGetReferrerByIdQuery();
  const [getBankByReferrerId] = useLazyGetReferrerBankQuery();
  const [createReferrerBank] = useCreateReferrerBankMutation();
  const [updateBankDetails] = useUpdateBankDetailsMutation();

  // Hook to fetch referrer details by ID
  useEffect(() => {
    if (referrer?.id) {
      fetchReferrerDetailsById(referrer.id);
    }
  }, [referrer?.id]);

  // Hook to fetch companies
  useEffect(() => {
    if (!companyOptions?.length) {
      fetchCompany();
    }
  }, [companyOptions]);

  // Function to fetch companies
  const fetchCompany = async () => {
    const response = await getCompanies({}).unwrap();

    if (response?.data?.length > 0) {
      const formattedCompanies = response.data.map((c: any) => ({
        label: c.name,
        value: c.id,
      }));
      setCompanyOptions(formattedCompanies);
    }
  };

  // Function to fetch referrer details by ID then fetch and add bank data
  const fetchReferrerDetailsById = async (referrerId: string) => {
    const response = await getReferrerById({ referrerId }).unwrap();

    if (response?.data?.referrerId) {
      let combinedReferrerData: ReferrerData | null = null;

      combinedReferrerData = {
        id: response.data.referrerId || '',
        userDetails: {
          id: response.data.referrerId || '',
          email: response.data.email || '',
          name: response.data.username || '',
          companyId: response.data.companyId || '',
        },
        commissionDetails: {
          commissionPercentage: createFormattedPercentValue(
            response.data.commissionPercentage * 100 || 5,
          ),
          commissionType: response.data.type || '',
          commissionExpiry: response.data.expiryDate || null,
          numberOfCohorts: response.data.numberOfCohorts || 0,
        },
      };

      // Fetch bank details and update referrer details
      const bankResponse = await getBankByReferrerId({
        referrerId: referrerId,
      }).unwrap();
      if (bankResponse.data) {
        combinedReferrerData.bankDetails = {
          id: bankResponse.data.id || '',
          bankName: bankResponse.data.bankName || '',
          bankAddress: bankResponse.data.bankAddress || '',
          swiftCode: bankResponse.data.swiftCode || '',
          iban: bankResponse.data.ibanCode || '',
          accountNumber: bankResponse.data.accountNumber || '',
          routingNumber: bankResponse.data.routingNumber || '',
        };
      }

      setReferrerDetails(combinedReferrerData);
    }
  };

  // Function to search referrers
  const searchReferrers = async (search: string) => {
    try {
      const response = await getUsers({
        pageNumber: 0,
        pageSize: 10,
        search,
        sort: 'email',
        sortOrder: 'ASC',
      }).unwrap();

      if (response.data) {
        const searchOptions = response.data.items.map((item: any) => ({
          id: item?.id || '',
          email: item?.email || '',
          name: item?.username || '',
          companyId: item?.company?.id || '',
        }));
        setEmailOptions(searchOptions);
      } else {
        setEmailOptions([]);
      }
    } catch (err) {
      console.error('Error while fetching users :', err);
      setEmailOptions([]);
    }
  };

  // Function to debounce search
  const debouncedSearch = useDebounce((value: string) => {
    if (value.trim()) {
      searchReferrers(value);
    } else {
      setEmailOptions([]);
    }
  }, SEARCH_DEBOUNCE_TIME);

  // Handle email input change
  const handleEmailInputChange = (event: React.SyntheticEvent, value: string) => {
    setEmailInputValue(value);
    debouncedSearch(value);
  };

  // Handle email selection
  const handleEmailSelection = (event: React.SyntheticEvent, value: string | null) => {
    const emailValue = value || '';
    const user = emailOptions.find((option) => option.email === emailValue);

    setReferrerDetails((prev) => ({
      ...prev,
      userDetails: {
        ...prev.userDetails,
        id: user?.id || '',
        email: emailValue,
        name: user?.name || '',
        companyId: user?.companyId || '',
      },
    }));

    setEmailInputValue(emailValue);
  };

  // Handle email input blur
  const handleEmailBlur = () => {
    if (emailInputValue && emailInputValue !== referrerDetails?.userDetails.email) {
      handleUpdateReferrerDetails('email', emailInputValue);
    }
  };

  // Handle commission percentage input (only allow numeric values)
  const handleCommissionPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handlePercentageChange(
      e,
      (formattedValue) => {
        handleUpdateCommissionDetails('commissionPercentage', formattedValue);
      },
      25,
    );
  };

  // Handle number of cohorts input (only allow numeric values)
  const handleCommissionNumberOfCohortsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || '0';
    // Allow only numbers
    if (!isNaN(Number(value))) {
      handleUpdateCommissionDetails('numberOfCohorts', Number(value));
    }
  };

  // Handle commission expiry input (enforce YYYY-MM-DD format)
  const handleCommissionExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove all non-digit characters

    // Limit to 8 digits (YYYYMMDD)
    if (value.length > 8) {
      value = value.slice(0, 8);
    }

    // Format as YYYY-MM-DD
    let formattedValue = '';
    if (value.length >= 1) {
      formattedValue = value.slice(0, 4); // YYYY
    }
    if (value.length >= 5) {
      formattedValue += '-' + value.slice(4, 6); // YYYY-MM
    }
    if (value.length >= 7) {
      formattedValue += '-' + value.slice(6, 8); // YYYY-MM-DD
    }

    handleUpdateCommissionDetails('commissionExpiry', formattedValue);
  };

  // Function to update referrer details
  const handleUpdateReferrerDetails = (key: keyof ReferrerDetails, value: string) => {
    let userDetails = { ...referrerDetails?.userDetails };
    userDetails[key] = value;
    setReferrerDetails({
      ...referrerDetails,
      userDetails,
    });
  };

  // Function to update commission details
  const handleUpdateCommissionDetails = <K extends keyof CommissionDetails>(
    key: K,
    value: CommissionDetails[K],
  ) => {
    let commissionDetails = { ...referrerDetails?.commissionDetails };
    commissionDetails[key] = value;
    setReferrerDetails({
      ...referrerDetails,
      commissionDetails,
    });
  };

  // Function to update bank details
  const handleUpdateBankDetails = (key: keyof BankDetails, value: string) => {
    let bankDetails = { ...referrerDetails?.bankDetails };
    bankDetails[key] = value;
    setReferrerDetails({
      ...referrerDetails,
      bankDetails: bankDetails as BankDetails,
    });
  };

  // Function to validate referrer data
  const validateReferrerDetails = () => {
    const isReferrerDetailsValid =
      Boolean(referrerDetails?.userDetails?.email) &&
      Boolean(referrerDetails?.userDetails?.name) &&
      Boolean(referrerDetails?.userDetails?.companyId);

    const isCommissionDetailsValid =
      Boolean(referrerDetails?.commissionDetails?.commissionPercentage) &&
      Boolean(referrerDetails?.commissionDetails?.commissionType);

    return isReferrerDetailsValid && isCommissionDetailsValid;
  };

  // Function to validate bank data
  const validateBankDetails = () => {
    const isBankDetailsValid =
      Boolean(referrerDetails?.bankDetails?.bankName) &&
      Boolean(referrerDetails?.bankDetails?.bankAddress) &&
      Boolean(referrerDetails?.bankDetails?.swiftCode) &&
      Boolean(referrerDetails?.bankDetails?.iban) &&
      Boolean(referrerDetails?.bankDetails?.accountNumber);

    return isBankDetailsValid && referrerDetails?.id;
  };

  // Function to handle form submission for referrer data
  const handleSubmitReferrerData = async () => {
    if (!referrerDetails) return;

    if (referrerDetails?.id) {
      try {
        const payload: UpdateReferrerPayload = {
          referrerId: referrerDetails.id,
          commissionPercentage:
            stripPercentSymbol(referrerDetails.commissionDetails.commissionPercentage) / 100,
          type: referrerDetails.commissionDetails.commissionType,
          expiryDate: referrerDetails.commissionDetails.commissionExpiry || null,
          numberOfCohorts: referrerDetails.commissionDetails.numberOfCohorts || 0,
        };

        await updateReferrer(payload).unwrap();

        dispatch(
          setToastMessage({
            type: TOAST_TYPES.SUCCESS,
            message: `Referrer updated successfully`,
          }),
        );
      } catch (error) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message: `Could not update referrer, please retry.`,
          }),
        );
      }
    } else {
      try {
        const payload: CreateReferrerPayload = {
          companyId: referrerDetails.userDetails.companyId || '',
          userId: referrerDetails.userDetails.id || null,
          userName: referrerDetails.userDetails.name || '',
          email: referrerDetails.userDetails.email || '',
          commissionPercentage:
            stripPercentSymbol(referrerDetails.commissionDetails.commissionPercentage) / 100,
          type: referrerDetails.commissionDetails.commissionType || '',
          expiryDate: referrerDetails.commissionDetails.commissionExpiry || null,
          numberOfCohorts: referrerDetails.commissionDetails.numberOfCohorts || 0,
        };
        const response = await createReferrer(payload).unwrap();

        dispatch(
          setToastMessage({
            type: TOAST_TYPES.SUCCESS,
            message: `Referrer created successfully`,
          }),
        );
        if (response?.data?.referrerId) {
          const id = response.data.referrerId || '';
          fetchReferrerDetailsById(id);
        }
      } catch (error) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message:
              (error as { error: { message: string } })?.error.message ||
              `Could not create referrer, please retry.`,
          }),
        );
      }
    }
  };

  // Function to handle form submission for bank data
  const handleSubmitReferrerBank = async () => {
    if (!referrerDetails?.id) return;

    const payload: ReferrerBankPayload = {
      bankName: referrerDetails?.bankDetails?.bankName || '',
      bankAddress: referrerDetails?.bankDetails?.bankAddress || '',
      swiftCode: referrerDetails?.bankDetails?.swiftCode || '',
      ibanCode: referrerDetails?.bankDetails?.iban || '',
      accountNumber: referrerDetails?.bankDetails?.accountNumber || '',
      routingNumber: referrerDetails?.bankDetails?.routingNumber || '',
    };

    if (referrerDetails?.bankDetails?.id) {
      try {
        await updateBankDetails({
          bankId: referrerDetails.bankDetails.id,
          ...payload,
        }).unwrap();

        dispatch(
          setToastMessage({
            type: TOAST_TYPES.SUCCESS,
            message: `Bank details saved successfully`,
          }),
        );
      } catch (error) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message: `Could not update bank details, please retry.`,
          }),
        );
      }
    } else {
      try {
        await createReferrerBank({
          referrerId: referrerDetails?.id || '',
          referrerDetails: payload,
        }).unwrap();

        dispatch(
          setToastMessage({
            type: TOAST_TYPES.SUCCESS,
            message: `Bank details saved successfully`,
          }),
        );
      } catch (error) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message: `Could not update bank details, please retry.`,
          }),
        );
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        border: 'none',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
        position: 'absolute',
        top: 0,
        left: 0,
        backgroundColor: '#f5f5f5',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '0.5rem',
          mt: '0.5rem',
          mb: '1.5rem',
        }}
      >
        <Box
          sx={{
            cursor: 'pointer',
          }}
          onClick={() => {
            handleCloseReferrerDetails();
          }}
        >
          <Typography fontSize={'1rem'} fontWeight={500} sx={{ color: '#17191C59' }}>
            Referrer
          </Typography>
        </Box>
        <Typography fontSize="1rem" fontWeight={500} sx={{ color: '#1B1C17' }}>
          &gt; {referrer?.name ? referrer.name : 'New Referrer'}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
        }}
      >
        {/* Scrollable Container */}
        <Box
          sx={{
            overflowY: 'auto',
            height: '100%',
          }}
        >
          {/* User Details Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '1.25rem',
            }}
          >
            <Typography
              fontSize={'1rem'}
              fontWeight={700}
              sx={{ color: '#17191C', marginBottom: '1rem' }}
            >
              User Details
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
              }}
            >
              <Autocomplete
                disablePortal
                options={emailOptions.map((option) => option.email)}
                value={referrerDetails?.userDetails.email || null}
                inputValue={emailInputValue}
                onInputChange={handleEmailInputChange}
                onChange={handleEmailSelection}
                renderInput={(params) => (
                  <TextField {...params} label="Email" onBlur={handleEmailBlur} required />
                )}
                disabled={Boolean(referrer?.id)}
              />
              <TextField
                label="Name"
                value={referrerDetails?.userDetails.name}
                onChange={(e) => handleUpdateReferrerDetails('name', e.target.value)}
                disabled={Boolean(referrerDetails?.userDetails.id)}
              />
              {referrerDetails?.userDetails.id ? (
                <TextField
                  label="Company"
                  value={
                    companyOptions.find(
                      (option) => option.value === referrerDetails?.userDetails.companyId,
                    )?.label || ''
                  }
                  onChange={(e) => handleUpdateReferrerDetails('companyId', e.target.value)}
                  disabled={Boolean(referrerDetails?.userDetails.companyId)}
                />
              ) : (
                <SelectWithLabel
                  label="Company"
                  required
                  field="companyId"
                  labelId="company-select-label"
                  menuItems={companyOptions}
                  onChange={async (value) => {
                    await fetchCompany();
                    handleUpdateReferrerDetails('companyId', value);
                  }}
                  isAddNew={true}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  }}
                  value={referrerDetails?.userDetails.companyId}
                  selectStyles={{
                    height: '56px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    borderRadius: '0.25rem',
                    paddingRight: '0.5rem',
                  }}
                  renderValue={(value) => {
                    if (value)
                      return companyOptions.find((option) => option.value === value)?.label || '';
                    return '';
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Commission Details Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '1.25rem',
            }}
          >
            <Typography
              fontSize={'1rem'}
              fontWeight={700}
              sx={{ color: '#17191C', marginBottom: '1rem' }}
            >
              Commission Details
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
              }}
            >
              <TextField
                label="Commission Percentage"
                value={referrerDetails?.commissionDetails.commissionPercentage || ''}
                onChange={handleCommissionPercentageChange}
              />
              <Select
                value={referrerDetails?.commissionDetails.commissionType || ''}
                onChange={(e) => handleUpdateCommissionDetails('commissionType', e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <Typography sx={{ color: 'text.secondary' }}>Type</Typography>;
                  }
                  const option = referrerTypeOptions.find((opt) => opt.value === selected);
                  return option?.label || selected;
                }}
                sx={{ minWidth: '100%' }}
              >
                {referrerTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {referrerDetails?.commissionDetails.commissionType === ReferrerType.COHORT_BASED ? (
                <TextField
                  label="Number of Cohorts"
                  value={referrerDetails?.commissionDetails.numberOfCohorts || 0}
                  onChange={handleCommissionNumberOfCohortsChange}
                  inputProps={{
                    maxLength: 10,
                  }}
                />
              ) : (
                <></>
              )}
              <TextField
                label="Expiry Date (Optional)"
                placeholder="YYYY-MM-DD"
                value={referrerDetails?.commissionDetails.commissionExpiry || ''}
                onChange={handleCommissionExpiryChange}
                inputProps={{
                  maxLength: 10,
                }}
              />
            </Box>
          </Box>

          {/* Save and Cancel Buttons for Referrer Section */}
          <Stack
            sx={{
              height: '4rem',
            }}
            direction={'row'}
            justifyContent={'flex-end'}
            gap={'0.5rem'}
            py={'0.5rem'}
          >
            <Button
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              variant="outlined"
              onClick={handleCloseReferrerDetails}
              sx={{
                borderRadius: '2rem',
                padding: '0.625rem 1.5rem',
                alignItems: 'center',
                textTransform: 'none',
                color: '#2F736E',
              }}
            >
              Cancel
            </Button>

            <Button
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              sx={{
                borderRadius: '2rem',
                backgroundColor: '#2F736E',
                color: '#fff',
                padding: '0.625rem 1.5rem',
                alignItems: 'center',
                textTransform: 'none',
              }}
              disabled={!validateReferrerDetails()}
              onClick={handleSubmitReferrerData}
              variant="contained"
            >
              Save
            </Button>
          </Stack>

          {/* Bank Details Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '1.25rem',
            }}
          >
            <Typography
              fontSize={'1rem'}
              fontWeight={700}
              sx={{ color: '#17191C', marginBottom: '1rem' }}
            >
              Bank Details
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
              }}
            >
              <TextField
                label="Bank Name"
                value={referrerDetails?.bankDetails?.bankName || ''}
                onChange={(e) => handleUpdateBankDetails('bankName', e.target.value)}
              />
              <TextField
                label="Bank Address"
                value={referrerDetails?.bankDetails?.bankAddress || ''}
                onChange={(e) => handleUpdateBankDetails('bankAddress', e.target.value)}
              />
              <TextField
                label="Swift Code"
                value={referrerDetails?.bankDetails?.swiftCode || ''}
                onChange={(e) => handleUpdateBankDetails('swiftCode', e.target.value)}
              />
              <TextField
                label="IBAN"
                value={referrerDetails?.bankDetails?.iban || ''}
                onChange={(e) => handleUpdateBankDetails('iban', e.target.value)}
              />
              <TextField
                label="Account Number"
                value={referrerDetails?.bankDetails?.accountNumber || ''}
                onChange={(e) => handleUpdateBankDetails('accountNumber', e.target.value)}
              />
              <TextField
                label="Routing Number"
                value={referrerDetails?.bankDetails?.routingNumber ?? ''}
                onChange={(e) => handleUpdateBankDetails('routingNumber', e.target.value)}
              />
            </Box>
          </Box>

          {/* Save and Cancel Buttons for Bank Section */}
          <Stack
            sx={{
              height: '4rem',
            }}
            direction={'row'}
            justifyContent={'flex-end'}
            gap={'0.5rem'}
            py={'0.5rem'}
          >
            <Button
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              variant="outlined"
              onClick={handleCloseReferrerDetails}
              sx={{
                borderRadius: '2rem',
                padding: '0.625rem 1.5rem',
                alignItems: 'center',
                textTransform: 'none',
                color: '#2F736E',
              }}
            >
              Cancel
            </Button>

            <Button
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              sx={{
                borderRadius: '2rem',
                backgroundColor: '#2F736E',
                color: '#fff',
                padding: '0.625rem 1.5rem',
                alignItems: 'center',
                textTransform: 'none',
              }}
              disabled={!validateBankDetails()}
              onClick={handleSubmitReferrerBank}
              variant="contained"
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ReferrerSetupForm;
